<template>
  <div class="breadcrumb-nav">
    <!-- Home Link. On home + desktop, the label carries the role too, as one
         string — not a second element next to it, which needed its font-size,
         line-height and spacing matched to the wordmark by hand and never
         quite landed right across three attempts. One label sidesteps all of
         that: whatever TextLink renders this at, both halves get it exactly
         alike, because it's the same text node. Every other page already
         states what this site is through the trailing crumb (Library, a doc
         title, a course); home has nothing there, and the hero's rotating
         lines only name the profession on one of seven loads — this is the
         one place it's guaranteed to show. -->
    <TextLink
      class="nav-item nav-item--home"
      :class="{ active: isHome }"
      :label="wordmarkLabel"
      route="/"
      :isSvg="false"
      iconsize="20"
    />
    <!-- :unicode="!isHome ? '←' : false" -->

    <!-- <TextLink
            class="wordmark"
            :style="isMobileScreen ? 'text-decoration: none' : ''"
            :label="isMobileScreen ? 'Jacques Ramphal' : 'Jacques Ramphal'"
            route="/"
            v-show="!menuOpen"
          /> -->

    <!-- Dynamic Breadcrumb Trail. On phones it's its own lane beside the
         pinned home link: one row, no wrapping, scrolling sideways, with a
         fade on whichever edge has more behind it. Only the current page
         shows; anything between home and it collapses to an ellipsis that
         opens the full path in place. Desktop shows the whole trail. -->
    <div
      v-if="!isHome && crumbs.length"
      ref="trail"
      class="breadcrumb-trail"
      :class="{ 'fade-start': fadeStart, 'fade-end': fadeEnd }"
      @scroll.passive="updateFades"
    >
      <DynamicText class="breadcrumb-sep" as="p" text="/" />
      <template v-if="collapsed">
        <button
          type="button"
          class="breadcrumb-more"
          aria-label="Show full path"
          :aria-expanded="'false'"
          @click.stop="expand"
        >
          …
        </button>
        <DynamicText class="breadcrumb-sep" as="p" text="/" />
      </template>
      <template v-for="(crumb, i) in visibleCrumbs">
        <router-link
          v-if="crumb.current"
          :key="`current-${crumb.route}`"
          :to="crumb.route"
          class="nav-item active"
          aria-current="page"
        >
          {{ crumb.label }}
        </router-link>
        <TextLink
          v-else
          :key="`link-${crumb.route}`"
          class="nav-item"
          :label="crumb.label"
          :route="crumb.route"
        />
        <DynamicText
          v-if="i < visibleCrumbs.length - 1"
          :key="`sep-${i}`"
          class="breadcrumb-sep"
          as="p"
          text="/"
        />
      </template>
    </div>
  </div>
</template>

<script>
import workData from '../assets/data/work.json';
import DynamicText from '../components/text/DynamicText.vue';
import TextLink from '../components/text/TextLink.vue';
import { getDocRecordById, getDocTitle, isNumericRouteParam } from '@/utils/docRegistry';
import {
  getChapterContext,
  getCourseBySlug,
  getDefaultCourse,
  isLibraryCollection,
} from '@/utils/courseRegistry';
export default {
  name: 'BreadCrumb',
  components: { DynamicText, TextLink },
  props: {
    isDesktopScreen: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      pageTitle: '',
      // Phones: whether the ellipsis has been opened into the full path.
      expanded: false,
      fadeStart: false,
      fadeEnd: false,
    };
  },
  computed: {
    isHome() {
      return this.$route?.path === '/';
    },
    // Phones abbreviate to JR on every page but home, so the trail gets the
    // room. Home has no trail, and the name is the point of it there.
    wordmarkLabel() {
      return !this.isDesktopScreen && !this.isHome ? 'JR' : 'Jacques Ramphal';
    },
    // Every crumb after home, in order. The last one marked `current` is the
    // page you're on.
    crumbs() {
      const list = [];
      // A collection that's listed in the Library sits under it, so the trail
      // reads Library / Mindset / the piece, the way the Library shows it.
      if (this.courseContext) {
        const { title, slug } = this.courseContext.course;
        if (isLibraryCollection(slug)) list.push({ label: 'Library', route: '/library' });
        list.push({ label: title, route: `/collection/${slug}` });
      } else if (this.isLibraryOrDeeper) {
        list.push({ label: 'Library', route: '/library', current: this.isLibrary });
      } else if (this.courseHub) {
        if (isLibraryCollection(this.courseHub.slug)) list.push({ label: 'Library', route: '/library' });
        list.push({ label: this.courseHub.title, route: this.$route.path, current: true });
      }
      if (this.isProjectOrDoc && this.pageTitle) {
        list.push({ label: this.pageTitle, route: this.$route.path, current: true });
      }
      return list;
    },
    collapsed() {
      return !this.isDesktopScreen && !this.expanded && this.crumbs.length > 1;
    },
    visibleCrumbs() {
      return this.collapsed ? this.crumbs.slice(-1) : this.crumbs;
    },
    isLibrary() {
      return this.$route?.path === '/library';
    },
    isLibraryOrDeeper() {
      // Show Library in breadcrumb for library page and all doc pages
      return this.$route?.path?.startsWith('/library') || this.$route?.path?.startsWith('/doc/');
    },
    isProjectOrDoc() {
      return this.$route?.meta?.dynamicTitle || !!this.$route?.meta?.title;
    },
    // When the current /doc/ page is a course chapter, resolve its course so the
    // breadcrumb points back to the course hub instead of the Library.
    courseContext() {
      const path = this.$route?.path || '';
      if (
        !path.startsWith('/doc/') &&
        !path.startsWith('/secured/doc/') &&
        !path.startsWith('/session/')
      )
        return null;

      const param = (this.$route.params.slug || this.$route.params.id || '').toString().trim();
      if (!param) return null;

      let slug = param;
      if (isNumericRouteParam(param)) {
        const record = getDocRecordById(parseInt(param, 10));
        slug = record?.slug || '';
      }

      return slug ? getChapterContext(slug) : null;
    },
    // On a collection hub page (/collection/:slug, or the older /course and
    // /course/:slug), resolve the collection so it
    // shows as the current crumb (Home / Course Title).
    courseHub() {
      const path = (this.$route?.path || '').toLowerCase();
      if (!path.startsWith('/course') && !path.startsWith('/collection')) return null;
      const slug = (this.$route.params.slug || '').toString().trim();
      return slug ? getCourseBySlug(slug) : getDefaultCourse();
    },
  },
  mounted() {
    // The fades depend on the trail's width, which changes with the title,
    // the expanded state and the window.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.updateFades());
      this.observeTrail();
    }
  },
  updated() {
    this.observeTrail();
    this.updateFades();
  },
  beforeUnmount() {
    this.resizeObserver?.disconnect();
    document.removeEventListener('click', this.collapseOnOutsideClick);
  },
  async created() {
    if (this.$route) {
      await this.updatePageTitle();
    }
  },
  watch: {
    $route: {
      immediate: true,
      deep: true,
      async handler(newRoute, oldRoute) {
        if (newRoute) {
          // Only update if route path or params changed
          if (
            !oldRoute ||
            newRoute.path !== oldRoute.path ||
            JSON.stringify(newRoute.params) !== JSON.stringify(oldRoute?.params)
          ) {
            this.collapse();
            // Wait for route to be fully updated
            await this.$nextTick();
            await this.updatePageTitle();
          }
        }
      },
    },
  },
  methods: {
    expand() {
      this.expanded = true;
      document.addEventListener('click', this.collapseOnOutsideClick);
    },
    collapse() {
      this.expanded = false;
      document.removeEventListener('click', this.collapseOnOutsideClick);
    },
    collapseOnOutsideClick(e) {
      if (!this.$el.contains(e.target)) this.collapse();
    },
    observeTrail() {
      const trail = this.$refs.trail;
      if (!this.resizeObserver || trail === this.observedTrail) return;
      this.resizeObserver.disconnect();
      if (trail) this.resizeObserver.observe(trail);
      this.observedTrail = trail;
    },
    // Fade an edge only while there's more text behind it. The 1px slack
    // absorbs subpixel widths that would otherwise leave a fade showing at
    // the very end of the scroll.
    updateFades() {
      const trail = this.$refs.trail;
      const start = !!trail && trail.scrollLeft > 1;
      const end = !!trail && trail.scrollLeft + trail.clientWidth < trail.scrollWidth - 1;
      if (start !== this.fadeStart) this.fadeStart = start;
      if (end !== this.fadeEnd) this.fadeEnd = end;
    },
    // Convert kebab-case to readable format with spaces and capitalize all words (Title Case)
    formatSlugToReadable(slug) {
      if (!slug) return '';

      // The doc's own title when the Library has one; otherwise the slug in
      // sentence case, like every title on the site.
      const title = getDocTitle(slug);
      if (title) return title;
      const text = slug.split('-').filter(Boolean).join(' ');
      return text.charAt(0).toUpperCase() + text.slice(1);
    },

    async updatePageTitle() {
      if (!this.$route) {
        this.pageTitle = '';
        return;
      }

      // Reset title first
      this.pageTitle = '';

      if (this.$route.meta?.title) {
        this.pageTitle = this.$route.meta.title;
      } else if (this.$route.path.startsWith('/work/')) {
        const workId = parseInt(this.$route.params.id);
        const work = workData.entries.find((entry) => entry.id === workId);
        this.pageTitle = work ? work.title : 'Work';
      } else if (
        this.$route.path.startsWith('/doc/') ||
        this.$route.path.startsWith('/secured/doc/') ||
        this.$route.path.startsWith('/session/')
      ) {
        // Course chapters carry a short title in the manifest; prefer it so the
        // crumb reads "Arrive" rather than the full slug "Still Yourself Arrive".
        if (this.courseContext?.current?.title) {
          this.pageTitle = this.courseContext.current.title;
          return;
        }

        // Use the slug directly from the route for cleaner breadcrumbs
        const param = (this.$route.params.slug || this.$route.params.id || '').toString().trim();

        if (!param) {
          this.pageTitle = 'document';
          return;
        }

        const isNumeric = isNumericRouteParam(param);

        if (isNumeric) {
          // For numeric IDs, try to get the slug from the record
          const docId = parseInt(param, 10);
          const record = getDocRecordById(docId);
          const slug = record?.slug || `doc ${docId}`;
          this.pageTitle = this.formatSlugToReadable(slug);
        } else {
          // Use the slug directly and format with spaces
          this.pageTitle = this.formatSlugToReadable(param);
        }
      } else {
        this.pageTitle = '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* The separator renders as a <p>; it takes the row's line height so it can't
   make the row taller than the links and nudge the wordmark down. */
.breadcrumb-sep {
  line-height: inherit;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  min-inline-size: 0;
  max-inline-size: 100%;
}

.breadcrumb-trail {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  flex-wrap: wrap;

  // Phones: one row that scrolls sideways under the pinned home link.
  @media only screen and (max-width: 767px) {
    --fade: var(--spacing-md);
    flex-wrap: nowrap;
    min-inline-size: 0;
    overflow-x: auto;
    // A scrolling box clips vertically too, which cut off the links'
    // underlines. Room for them, given back so the row's height holds.
    padding-block: var(--spacing-xxs);
    margin-block: calc(-1 * var(--spacing-xxs));
    overscroll-behavior-x: contain;
    scrollbar-width: none;
    -webkit-mask-image: linear-gradient(
      to right,
      transparent,
      #000 var(--fade-start-size, 0px),
      #000 calc(100% - var(--fade-end-size, 0px)),
      transparent
    );
    mask-image: linear-gradient(
      to right,
      transparent,
      #000 var(--fade-start-size, 0px),
      #000 calc(100% - var(--fade-end-size, 0px)),
      transparent
    );

    &::-webkit-scrollbar {
      display: none;
    }
    &.fade-start {
      --fade-start-size: var(--fade);
    }
    &.fade-end {
      --fade-end-size: var(--fade);
    }
  }
}

// Phones: crumbs sit closer, so more of the title fits before the fade.
.breadcrumb-nav,
.breadcrumb-trail {
  @media only screen and (max-width: 767px) {
    gap: var(--spacing-xxs);
  }
}

/* The ellipsis reads as a nav link: same face, size and colour. The padding
   widens the tap target and the matching negative margin gives the space
   back, so it sits at the same spacing as every other crumb. */
.breadcrumb-more {
  appearance: none;
  background: none;
  border: 0;
  padding: 0 var(--spacing-xxs);
  margin-inline: calc(-1 * var(--spacing-xxs));
  min-block-size: 44px;
  font: inherit;
  font-size: var(--nav-fontSize);
  line-height: 1;
  color: var(--foreground);
  cursor: pointer;
  flex-shrink: 0;
}

.nav-item {
  text-decoration: none;
  transition: opacity 0.2s ease;
  white-space: nowrap; // Prevent individual links from breaking
  flex-shrink: 0; // Prevent compression
  &:hover:not(.active) {
    opacity: 0.8;
  }

  &.active {
    // Current-page crumb: theme foreground, de-emphasised by opacity rather
    // than a hardcoded grey, so it tracks light and dark mode.
    color: var(--foreground) !important;
    // !important beats the global `a { opacity: 1 !important }` in typography.scss.
    opacity: 0.6 !important;
    cursor: default;
    pointer-events: none;
  }

  // The wordmark on home is the site's name, not a trail crumb: full ink, still
  // not a link to the page you're on.
  &--home.active {
    opacity: 1 !important;
  }
}
</style>
